import site from './site.json';
import { SITE_URL, absoluteUrl } from './url';

export type JsonLd = Record<string, unknown>;

const SCHEMA = 'https://schema.org';

/** The business itself. Shown on the home, about and contact pages. */
export function businessJsonLd(): JsonLd {
  return {
    '@context': SCHEMA,
    '@type': 'HomeAndConstructionBusiness',
    '@id': `${SITE_URL}/#business`,
    name: site.name,
    legalName: site.legalName,
    url: `${SITE_URL}/`,
    logo: absoluteUrl(site.logo),
    image: absoluteUrl(site.ogImage),
    description: site.description,
    telephone: site.telephone,
    email: site.email,
    address: {
      '@type': 'PostalAddress',
      addressLocality: site.address.locality,
      addressCountry: site.address.country,
    },
    areaServed: { '@type': 'Country', name: 'Nepal' },
    sameAs: site.sameAs,
  };
}

export interface Crumb {
  name: string;
  path: string;
}

export function breadcrumbJsonLd(crumbs: Crumb[]): JsonLd {
  return {
    '@context': SCHEMA,
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: absoluteUrl(crumb.path),
    })),
  };
}

const provider = () => ({
  '@type': 'HomeAndConstructionBusiness',
  name: site.name,
  url: `${SITE_URL}/`,
});

export function serviceJsonLd(input: { name: string; description: string; path: string }): JsonLd {
  return {
    '@context': SCHEMA,
    '@type': 'Service',
    name: input.name,
    description: input.description,
    url: absoluteUrl(input.path),
    provider: provider(),
    areaServed: { '@type': 'Country', name: 'Nepal' },
  };
}

export function productJsonLd(input: { name: string; description: string; path: string; image?: string }): JsonLd {
  return {
    '@context': SCHEMA,
    '@type': 'Product',
    name: input.name,
    description: input.description,
    url: absoluteUrl(input.path),
    ...(input.image ? { image: absoluteUrl(input.image) } : {}),
    // Vista products are supplied through our partner Vista Chemtech.
    ...(/^vista\b/i.test(input.name) ? { brand: { '@type': 'Brand', name: 'Vista Chemtech' } } : {}),
  };
}

const SECTION_LABELS: Record<string, string> = {
  products: 'Products',
  services: 'Services',
  projects: 'Projects',
  gallery: 'Gallery',
};

/** Home > Section > Page, derived from the URL. */
export function breadcrumbsFor(path: string, currentName: string): Crumb[] {
  const crumbs: Crumb[] = [{ name: 'Home', path: '/' }];
  if (path === '/') return crumbs;

  const [section] = path.split('/').filter(Boolean);
  if (SECTION_LABELS[section] && path !== `/${section}`) {
    crumbs.push({ name: SECTION_LABELS[section], path: `/${section}` });
  }
  crumbs.push({ name: currentName, path });
  return crumbs;
}
